/**
 * @fileoverview useFocusState Hook
 * 
 * Custom hook that manages all state and side effects for the Focus component.
 * Handles session focus logic, exit confirmation, and SessionViewer instance.
 * 
 * @module vtc/components/Focus/useFocusState
 */

import { useState, useEffect, useCallback } from 'react';
import useAppStore from '../../stores/app';
import useSessionStore from '../../stores/session';

/**
 * useFocusState Hook
 * 
 * Manages all state and logic for the Focus component including:
 * - Focused session data and viewer instance
 * - Exit confirmation logic
 * - Session transition handling
 * - Store subscriptions
 * 
 * @returns {Object} State and handlers for Focus component
 * @returns {Object} return.focusedSession - Currently focused session data
 * @returns {string} return.focusedSessionSid - Focused session ID
 * @returns {JSX.Element} return.focusViewer - Memoized SessionViewer component
 * @returns {boolean} return.showExitConfirmation - Whether to show exit confirmation modal
 * @returns {Function} return.handleTransfer - Transfer session handler
 * @returns {Function} return.handleEndSession - End session handler
 * @returns {Function} return.handleClose - Close focus view handler
 * @returns {Function} return.handleConfirmedExit - Confirmed exit handler
 * @returns {Function} return.handleCancelExit - Cancel exit handler
 */
export function useFocusState() {
  // ============================================================================
  // STORE SUBSCRIPTIONS
  // ============================================================================

  const closeDrawer = useAppStore((state) => state.closeDrawer);
  const setDrawerCloseConfirmation = useAppStore((state) => state.setDrawerCloseConfirmation);
  const drawerCloseConfirmation = useAppStore((state) => state.drawerCloseConfirmation);
  const focusedSession = useSessionStore((state) => state.focusedSession);
  const focusedSessionSid = useSessionStore((state) => state.focusedSessionSid);
  const clearFocusedSession = useSessionStore((state) => state.clearFocusedSession);
  const setTransferSessionSid = useSessionStore((state) => state.setTransferSessionSid);
  const setEndSessionSid = useSessionStore((state) => state.setEndSessionSid);
  const transitionSession = useSessionStore((state) => state.transitionSession);
  const setShowTransferNote = useSessionStore((state) => state.setShowTransferNote);

  // ============================================================================
  // LOCAL STATE
  // ============================================================================

  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handles session approved event from focus view
   * Transitions session from review to testing if needed
   */
  const handleSessionApproved = useCallback(async () => {
    await transitionSession(focusedSessionSid, 'testing');
  }, [focusedSessionSid, transitionSession]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Handles confirmed exit from focus view
   * Transitions session from review to waiting if needed
   */
  const handleConfirmedExit = useCallback(async () => {
    await clearFocusedSession();
    setDrawerCloseConfirmation(null);
    closeDrawer();
    setShowExitConfirmation(false);
  }, [
    focusedSession,
    focusedSessionSid,
    transitionSession,
    clearFocusedSession,
    setDrawerCloseConfirmation,
    closeDrawer,
  ]);

  /**
   * Handles exit attempt from focus view
   * Shows confirmation if session is in review
   */
  const handleExit = useCallback(() => {
    if (focusedSession?.location === 'review' && drawerCloseConfirmation) {
      setShowExitConfirmation(true);
    } else {
      handleConfirmedExit();
    }
  }, [focusedSession, handleConfirmedExit, drawerCloseConfirmation]);

  /**
   * Handles transfer action
   */
  const handleTransfer = useCallback(() => {
    setTransferSessionSid(focusedSessionSid);
  }, [focusedSessionSid, setTransferSessionSid]);

  /**
   * Handles end session action
   */
  const handleEndSession = useCallback(() => {
    setEndSessionSid(focusedSessionSid);
  }, [focusedSessionSid, setEndSessionSid]);

  /**
   * Handles close action (drawer close button)
   */
  const handleClose = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  /**
   * Handles cancel exit confirmation
   */
  const handleCancelExit = useCallback(() => {
    setShowExitConfirmation(false);
  }, []);

  /**
   * Handles incident report action
   */
  const handleIncidentReport = useCallback(() => {
    if (!focusedSessionSid) return;

    // Get the viewer ref from the session store
    const sessions = useSessionStore.getState().sessions;
    const viewerRef = sessions[focusedSessionSid]?.viewerRef;
    console.log(viewerRef);
    // Open the incident report modal via the SessionViewer ref
    if (viewerRef?.current?.openIncidentReportModal) {
      viewerRef.current.openIncidentReportModal();
    }
  }, [focusedSessionSid]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Set up drawer close confirmation when session is focused
   */
  useEffect(() => {
    if (focusedSession) {
      // Set up confirmation handler for drawer close
      setDrawerCloseConfirmation(handleExit);

      if (focusedSession.location === 'review' && focusedSession?.transfer?.note) {
        setShowTransferNote(true);
      }
    }
  }, [focusedSession]);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    focusedSession,
    focusedSessionSid,
    showExitConfirmation,
    // Handlers
    handleTransfer,
    handleEndSession,
    handleClose,
    handleIncidentReport,
    handleConfirmedExit,
    handleCancelExit,
  };
}
